import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { Boxes, Sparkles, RefreshCw, Eye, CheckCircle2 } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { useFeatureWizards } from '../hooks/useFeatureWizard';
import { useNavigate } from 'react-router-dom';
import FeatureWizard from './FeatureWizard';
import { getModernOrderListWizardSteps } from '../wizards/modernOrderListWizard';
import { getProductsWizardSteps } from '../wizards/productsWizard';
import { navigationGroups } from './ModernNavigation';
import { cn } from '../lib/utils';

interface WizardManagerProps {
  open: boolean;
  onClose: () => void;
}

interface WizardDefinition {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  getSteps: (translate: (key: string) => string) => any[];
  /**
   * Optional translation key for localized search keywords
   * (e.g. 'pos.wizards.modernOrderList.keywords')
   */
  keywordsKey?: string;
  route?: string;
}

const WizardManager: React.FC<WizardManagerProps> = ({ open, onClose }) => {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const { wizards, resetWizard, resetAllWizards, getWizardState } =
    useFeatureWizards();
  const [selectedWizard, setSelectedWizard] = useState<WizardDefinition | null>(
    null
  );
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const normalizeText = (value: string) =>
    value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/đ/g, 'd')
      .replace(/Đ/g, 'D')
      .toLowerCase();

  const getWizardSearchText = (wizard: WizardDefinition) => {
    const baseText = `${wizard.title} ${wizard.description}`;

    let stepsText = '';
    try {
      const steps = wizard.getSteps(translate);
      stepsText = steps
        .map((step: any) => {
          const stepTitle = (step && step.title) || '';
          const stepDescription = (step && step.description) || '';
          return `${stepTitle} ${stepDescription}`;
        })
        .join(' ');
    } catch (error) {
      // Fail gracefully if steps cannot be built for some reason
      console.error('Error building wizard search text', error);
    }

    const keywordsText = wizard.keywordsKey
      ? translate(wizard.keywordsKey)
      : '';

    return normalizeText(`${baseText} ${stepsText} ${keywordsText}`);
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Debounce the search term to avoid filtering on every key press
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 250);

    return () => window.clearTimeout(handle);
  }, [searchTerm]);

  const featureWizards: WizardDefinition[] = [
    {
      id: 'modernOrderList',
      title: translate('pos.wizards.modernOrderList.welcome.title'),
      description: translate(
        'pos.wizards.modernOrderList.welcome.description'
      ),
      icon: <Sparkles className="h-5 w-5" />,
      getSteps: () => getModernOrderListWizardSteps(translate),
      keywordsKey: 'pos.wizards.modernOrderList.keywords',
      // Modern order list now lives at the main orders route
      route: '/orders',
    },
    {
      id: 'products',
      title: translate('pos.wizards.products.welcome.title'),
      description: translate('pos.wizards.products.welcome.description'),
      icon: <Boxes className="h-5 w-5" />,
      getSteps: () => getProductsWizardSteps(translate),
      keywordsKey: 'pos.wizards.products.keywords',
      route: '/products',
    },
  ];

  // Build navigation-based wizards from the main navigation config
  const excludedNavIds = new Set(['orders', 'products']);
  const navWizards: WizardDefinition[] = navigationGroups
    .flatMap((group) =>
      group.items.map((item) => ({
        id: `nav_${item.id}`,
        title: translate(item.labelKey),
        description: translate(group.labelKey),
        icon: <item.icon className="h-5 w-5" />,
        // No dedicated wizard steps yet – Start will behave like Open
        getSteps: () => [],
        route: item.path,
      }))
    )
    .filter((wizard) => !excludedNavIds.has(wizard.id.replace(/^nav_/, '')));

  const availableWizards: WizardDefinition[] = [
    ...featureWizards,
    ...navWizards,
  ];

  const handleOpenWizard = (wizard: WizardDefinition) => {
    const steps = wizard.getSteps(translate);
    if (!steps || steps.length === 0) {
      // If no steps defined, just navigate directly
      handleNavigateToWizardRoute(wizard);
      return;
    }
    setSelectedWizard(wizard);
    setIsWizardOpen(true);
  };

  const handleCloseWizard = () => {
    setIsWizardOpen(false);
    setSelectedWizard(null);
  };

  const handleResetWizard = (wizardId: string) => {
    resetWizard(wizardId);
  };

  const handleResetAllWizards = () => {
    resetAllWizards();
  };

  const handleNavigateToWizardRoute = (wizard: WizardDefinition) => {
    if (wizard.route) {
      navigate(wizard.route);
      onClose();
    }
  };

  const filteredWizards = availableWizards.filter((wizard) => {
    if (!debouncedSearchTerm.trim()) {
      return false;
    }
    const term = normalizeText(debouncedSearchTerm);
    const wizardText = getWizardSearchText(wizard);

    return wizardText.includes(term);
  });

  const getWizardStatus = (wizardId: string) => {
    const state = getWizardState(wizardId);
    if (state.hasSeen) {
      return {
        status: 'completed' as const,
        label: translate('pos.common.action.completed'),
        icon: <CheckCircle2 className="h-4 w-4 text-green-600" />,
        color: 'default' as const,
      };
    }
    return {
      status: 'available' as const,
      label: translate('pos.common.action.available'),
      icon: <Eye className="h-4 w-4 text-primary" />,
      color: 'default' as const,
    };
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent
          className={cn(
            'max-w-2xl w-full',
            isMobile && 'h-screen max-h-screen'
          )}
        >
          <DialogHeader>
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <DialogTitle>
                {translate('pos.wizards.manager.title')}
              </DialogTitle>
            </div>
            <DialogDescription>
              {translate('pos.wizards.manager.description')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-4 max-h-[60vh] overflow-auto">
            <div className="mb-2">
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={translate(
                  'pos.wizards.manager.searchPlaceholder'
                )}
                className="w-full"
              />
            </div>

            {filteredWizards.map((wizard) => {
              const status = getWizardStatus(wizard.id);
              return (
                <div
                  key={wizard.id}
                  className="flex items-center gap-4 p-4 border rounded-lg hover:bg-accent transition-colors"
                >
                  <div className="flex-shrink-0">{wizard.icon}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{wizard.title}</p>
                    <p className="text-sm text-muted-foreground">
                      {wizard.description}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge
                      variant="outline"
                      className="flex items-center gap-1"
                    >
                      {status.icon}
                      {status.label}
                    </Badge>
                    {wizard.route && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleNavigateToWizardRoute(wizard)}
                      >
                        {translate('pos.common.action.open')}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenWizard(wizard)}
                    >
                      {status.status === 'completed'
                        ? translate('pos.common.action.review')
                        : translate('pos.common.action.start')}
                    </Button>
                    {status.status === 'completed' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleResetWizard(wizard.id)}
                        title={translate(
                          'pos.wizards.manager.resetWizard'
                        )}
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {Object.keys(wizards).length > 0 && (
            <div className="pt-4 border-t">
              <Button
                variant="outline"
                onClick={handleResetAllWizards}
                className="text-orange-600 border-orange-300 hover:bg-orange-50"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                {translate('pos.wizards.manager.resetAll')}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Individual Wizard Dialog */}
      {selectedWizard && (
        <FeatureWizard
          open={isWizardOpen}
          onClose={handleCloseWizard}
          title={selectedWizard.title}
          description={selectedWizard.description}
          featureId={selectedWizard.id}
          steps={selectedWizard.getSteps(translate)}
          onComplete={handleCloseWizard}
          onSkip={handleCloseWizard}
        />
      )}
    </>
  );
};

export default WizardManager;
